import {Fragment, useState} from 'react'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Chip from '@mui/material/Chip'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreIcon from '@mui/icons-material/MoreVert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import Modal from '../../shared/Modal'

import {formatDate} from '../../../lib/data'
import {firstLetterUpperCased, fullName} from '../../../lib/tools'
import {SENT, contactAction, contactChip, contactDetail, isRemovalRefused, removalPrompt} from '../../../lib/recovery'

const initials = invite => `${firstLetterUpperCased(invite.firstName)}${firstLetterUpperCased(invite.lastName)}`

const RecoveryContact = props => {
  const {invite, invites, pendingWraps} = props
  const [anchorEl, setAnchorEl] = useState(null)
  const [confirming, setConfirming] = useState('')
  const chip = contactChip({invite, pendingWraps})
  const action = contactAction(invite)
  const prompt = removalPrompt({invite, invites})

  const openMenu = event => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const confirm = mode => () => {
    closeMenu()
    setConfirming(mode)
  }

  const dismiss = () => {
    setConfirming('')
  }

  // A draft has never been sent, so there is nothing to warn about. Resending abandons the link in
  // the previous email, and nothing on the button says so.
  const send = () => {
    if (invite.status === SENT)
      setConfirming('resend')
    else
      props.sendInvitation({invite, path: action.path})
  }

  const resend = () => {
    props.sendInvitation({invite, path: action.path})
    dismiss()
  }

  const remove = () => {
    props.removeContact(invite)
    dismiss()
  }

  return (
    <Fragment>
      <Stack
        className='recovery-contact'
        component='div'
        sx={{
          alignItems: {sm: 'center'},
          bgcolor: 'Background',
          flexDirection: {xs: 'column', sm: 'row'},
          justifyContent: 'space-between'
        }}>
        <Stack className='summary' component='div' sx={{flexDirection: 'row'}}>
          <Avatar className='avatar'>{initials(invite)}</Avatar>
          <Stack className='details' component='div'>
            <Typography className='name' variant='body1'>{fullName(invite)}</Typography>
            <Typography className='content' variant='body2'>{invite.email}</Typography>
            <Typography className='content' variant='body2'>{contactDetail({invite, pendingWraps})}</Typography>
          </Stack>
        </Stack>
        <Stack className='controls' component='div' direction='row'>
          <Chip className='status' size='small' label={chip.label} color={chip.color} />
          {
            action && (
              <Button disabled={props.loading} onClick={send} size='small'>{action.label}</Button>
            )
          }
          <IconButton aria-label={`More options for ${fullName(invite)}`} onClick={openMenu} size='small'>
            <MoreIcon fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
        <MenuItem disabled={isRemovalRefused({invite, invites})} onClick={confirm('remove')}>
          <ListItemIcon>
            <DeleteIcon fontSize='small' />
          </ListItemIcon>
          Remove
        </MenuItem>
      </Menu>
      <Modal
        maxWidth='sm'
        open={confirming === 'remove'}
        handleClose={dismiss}
        buttonText='Close'
        title={`Remove ${fullName(invite)}?`}>
        <Typography gutterBottom>{prompt.body}</Typography>
        {prompt.warning && <Typography color='error' gutterBottom>{prompt.warning}</Typography>}
        <ButtonGroup direction='row'>
          <Button onClick={dismiss}>Keep {invite.firstName}</Button>
          <Button color='error' loading={props.loading} onClick={remove}>Remove</Button>
        </ButtonGroup>
      </Modal>
      <Modal
        maxWidth='sm'
        open={confirming === 'resend'}
        handleClose={dismiss}
        buttonText='Close'
        title={`Send ${invite.firstName} a new invitation?`}>
        <Typography gutterBottom>
          The link in the email we sent {formatDate(invite.sentAt)} will stop working straight away.
          If they still have it, they won't be able to use it.
        </Typography>
        <ButtonGroup direction='row'>
          <Button onClick={dismiss}>Keep the old one</Button>
          <Button color='primary' loading={props.loading} onClick={resend}>Send new invitation</Button>
        </ButtonGroup>
      </Modal>
    </Fragment>
  )
}

RecoveryContact.propTypes = {
  invite: PropTypes.object.isRequired,
  invites: PropTypes.array.isRequired,
  pendingWraps: PropTypes.array,
  sendInvitation: PropTypes.func.isRequired,
  removeContact: PropTypes.func.isRequired,
  loading: PropTypes.bool
}

export default RecoveryContact
